import type { SyncState } from './activitySync';
import {
  getOptionalAnalyticsAuthToken,
  getOrCreateAnalyticsClientId,
} from './analyticsIdentity';

type ActivityFeature = 'favorite' | 'view' | 'report';
type ActivityAction =
  | 'saved_local'
  | 'saved_account'
  | 'sync_pending'
  | 'sync_failed'
  | 'sync_recovered';

interface ActivityMetricPayload {
  feature: ActivityFeature;
  action: ActivityAction;
  sync_state: SyncState;
  authenticated: boolean;
  reference_no?: string;
  council?: string;
  metadata?: Record<string, unknown>;
}

const ENABLE_LOCAL_ACTIVITY_METRICS =
  process.env.NEXT_PUBLIC_ENABLE_LOCAL_ACTIVITY_METRICS === '1';

function normalizeMetricsPath(pathname: string): string {
  let path = (pathname || '').trim().replace(/\/+$/, '');
  if (!path) path = '/';

  const lower = path.toLowerCase();
  if (lower.endsWith('/api/v1/accounts')) {
    return path.slice(0, -'/api/v1/accounts'.length) + '/api/v1/activity-metrics';
  }
  if (lower.endsWith('/accounts')) {
    return path.slice(0, -'/accounts'.length) + '/activity-metrics';
  }
  if (path === '/') return '/api/v1/activity-metrics';
  if (lower.endsWith('/api')) return path + '/v1/activity-metrics';
  if (lower.endsWith('/api/v1')) return path + '/activity-metrics';
  return path + '/activity-metrics';
}

function getActivityMetricsUrl(): string | null {
  if (
    typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname) &&
    !ENABLE_LOCAL_ACTIVITY_METRICS
  ) {
    return null;
  }

  // Keep best-effort activity telemetry same-origin. A missing CORS header
  // must never become a visible browser console error for a save/view event.
  if (typeof window !== 'undefined') return '/api/activity-metrics';

  const rawBase = process.env.NEXT_PUBLIC_API_URL?.trim();
  if (!rawBase) return null;

  if (rawBase.startsWith('/')) {
    return normalizeMetricsPath(rawBase);
  }

  try {
    const url = new URL(rawBase);
    return url.origin + normalizeMetricsPath(url.pathname);
  } catch {
    return null;
  }
}

export function trackActivityMetric(payload: ActivityMetricPayload): void {
  if (typeof window === 'undefined') return;

  const clientId = getOrCreateAnalyticsClientId();
  if (!clientId) return;
  const metricsUrl = getActivityMetricsUrl();
  if (!metricsUrl) return;

  const body = JSON.stringify({
    ...payload,
    client_id: clientId,
    occurred_at: new Date().toISOString(),
    path: window.location.pathname,
  });

  void postActivityMetric(metricsUrl, body);
}

async function postActivityMetric(metricsUrl: string, body: string): Promise<void> {
  const token = await getOptionalAnalyticsAuthToken();

  try {
    if (!token && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(metricsUrl, blob);
      return;
    }
  } catch {
    // Fall through to fetch.
  }

  void fetch(metricsUrl, {
    method: 'POST',
    headers: token
      ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      : { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // Metrics are best-effort only.
  });
}
